import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './MarqueeText.scss';

/**
 * Design System MarqueeText Component
 *
 * Displays text that scrolls horizontally when it overflows its container.
 * When text fits, it renders as static text. Automatically rechecks on resize.
 *
 * @example
 * // Basic usage
 * <MarqueeText text="This is a very long holiday name that might overflow" />
 *
 * @example
 * // Custom speed and pause
 * <MarqueeText text="Scrolling text" speed={10} pauseDuration={25} />
 *
 * @example
 * // Custom styling
 * <MarqueeText text="Styled text" className="my-title" style={{ fontSize: 20 }} />
 */
const MarqueeText = ({
    text,
    speed = 8,
    pauseDuration = 20,
    gap = 40,
    className = '',
    style = {},
    ...props
}) => {
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const checkOverflow = useCallback(() => {
        if (containerRef.current && textRef.current) {
            setIsOverflowing(textRef.current.scrollWidth > containerRef.current.clientWidth);
        }
    }, []);

    useEffect(() => {
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [text, checkOverflow]);

    const baseClassName = 'ds-marquee-text';
    const combinedClassName = [
        baseClassName,
        isOverflowing ? `${baseClassName}--scrolling` : '',
        className
    ].filter(Boolean).join(' ');

    const animationStyle = isOverflowing
        ? { animationDuration: `${speed}s`, paddingRight: `${gap}px` }
        : {};

    return (
        <div
            ref={containerRef}
            className={combinedClassName}
            style={style}
            title={text}
            {...props}
        >
            <span
                ref={textRef}
                className={`${baseClassName}__inner`}
                style={{
                    ...animationStyle,
                    '--marquee-pause': `${pauseDuration}%`,
                    '--marquee-scroll': `${100 - pauseDuration}%`,
                }}
            >
                {text}
            </span>
        </div>
    );
};

MarqueeText.propTypes = {
    /** The text content to display */
    text: PropTypes.string.isRequired,

    /** Animation duration in seconds (lower = faster) */
    speed: PropTypes.number,

    /** Percentage of animation time spent paused at the start (0-50) */
    pauseDuration: PropTypes.number,

    /** Gap in pixels after text before it loops/resets */
    gap: PropTypes.number,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles applied to the container */
    style: PropTypes.object,
};

export default MarqueeText;
